import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import {
  Wrench, Phone, MapPin, Clock, Camera, RefreshCw, ShieldCheck,
  FileText, ChevronDown, ChevronUp, CheckCircle2, Download, History, MessageCircle,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { PortalStatusHistory, PortalDiagnosis } from "@/components/portal/PortalHistoryDiagnosis";
import { PortalQuotes } from "@/components/portal/PortalQuotes";
import { PortalPaymentProof } from "@/components/portal/PortalPaymentProof";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const publicApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

function shade(hex, amount) {
  const h = (hex || "").replace("#", "");
  if (h.length !== 6) return hex;
  const num = parseInt(h, 16);
  let r = (num >> 16) & 0xff, g = (num >> 8) & 0xff, b = num & 0xff;
  r = Math.max(0, Math.min(255, Math.round(r + 255 * amount)));
  g = Math.max(0, Math.min(255, Math.round(g + 255 * amount)));
  b = Math.max(0, Math.min(255, Math.round(b + 255 * amount)));
  return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`;
}
function timeAgo(iso) {
  if (!iso) return "";
  const secs = Math.max(1, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
  if (secs < 60) return "hace unos segundos";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `hace ${mins} min`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `hace ${hrs} h`;
  return `hace ${Math.floor(hrs / 24)} d`;
}
function fmtMxn(n) {
  return new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN" }).format(Number(n || 0));
}

const STATUS_TONE = {
  recibido: "bg-slate-100 text-slate-800",
  en_diagnostico: "bg-blue-100 text-blue-800",
  pendiente_autorizacion: "bg-amber-100 text-amber-800",
  autorizado: "bg-teal-100 text-teal-800",
  en_proceso: "bg-indigo-100 text-indigo-800",
  pausa: "bg-yellow-100 text-yellow-800",
  listo_para_entrega: "bg-emerald-100 text-emerald-800",
  entregado: "bg-green-100 text-green-800",
  cancelado: "bg-red-100 text-red-800",
};

export default function PublicPortalOrder() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loadedAt, setLoadedAt] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [lightbox, setLightbox] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [history, setHistory] = useState(null);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [decisionNotice, setDecisionNotice] = useState(null);

  const fetchOrder = useCallback(async (silent = false) => {
    if (!silent) setRefreshing(true);
    try {
      const { data } = await publicApi.get(`/public/portal/${token}`);
      setData(data);
      setNotFound(false);
      setLoadedAt(new Date());
    } catch (err) {
      if (err.response?.status === 404) setNotFound(true);
    } finally {
      if (!silent) setRefreshing(false);
    }
  }, [token]);

  useEffect(() => { fetchOrder(); }, [fetchOrder]);
  useEffect(() => {
    if (notFound) return;
    const id = setInterval(() => fetchOrder(true), 60_000);
    return () => clearInterval(id);
  }, [fetchOrder, notFound]);

  useEffect(() => {
    const color = data?.tenant_branding?.primary_color || "#0F172A";
    document.documentElement.style.setProperty("--tenant-primary", color);
    document.documentElement.style.setProperty("--tenant-primary-hover", shade(color, -0.08));
    document.documentElement.style.setProperty("--tenant-primary-contrast", "#ffffff");
    if (data?.order?.folio) document.title = `Folio ${data.order.folio} · Portal`;
    let meta = document.querySelector('meta[name="robots"]');
    if (!meta) { meta = document.createElement("meta"); meta.name = "robots"; document.head.appendChild(meta); }
    meta.setAttribute("content", "noindex, nofollow");
  }, [data]);

  const hasPending = !!data?.quotes?.some((q) => q.status === "pending");
  useEffect(() => {
    if (hasPending && window.location.hash === "#cotizacion") {
      document.getElementById("cotizacion")?.scrollIntoView({ behavior: "smooth" });
    }
  }, [hasPending]);

  const loadHistory = async () => {
    if (history) { setHistoryOpen(!historyOpen); return; }
    setLoadingHistory(true);
    try {
      const { data: h } = await publicApi.get(`/public/portal/${token}/vehicle-history`);
      setHistory(h);
      setHistoryOpen(true);
    } catch (_) { /* noop */ }
    finally { setLoadingHistory(false); }
  };

  if (notFound) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-6" data-testid="portal-not-found">
        <div className="max-w-md text-center">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-slate-100 text-slate-500 mb-5">
            <ShieldCheck className="h-6 w-6" />
          </div>
          <h1 className="text-2xl mb-2 tracking-tight">Enlace no disponible</h1>
          <p className="text-sm text-muted-foreground">
            El enlace que intentas consultar no está activo. Verifica que sea el más reciente o contacta a tu taller.
          </p>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen bg-slate-50 p-6 max-w-3xl mx-auto space-y-4">
        <Skeleton className="h-24" /><Skeleton className="h-48" /><Skeleton className="h-64" />
      </div>
    );
  }

  const order = data.order || {};
  const branch = data.branch || {};
  const vehicle = data.vehicle || {};
  const brand = data.tenant_branding || {};
  const evidences = data.evidences_public || [];

  const uploadProof = async (file) => {
    const fd = new FormData();
    fd.append("file", file);
    await publicApi.post(`/public/portal/${token}/payment-proofs`, fd);
    await fetchOrder(true);
  };
  const decideQuote = async (quoteId, decision, comment) => {
    const { data: res } = await publicApi.post(`/public/portal/${token}/quotes/${quoteId}/decision`, { decision, comment });
    setDecisionNotice({ decision, number: res.number });
    await fetchOrder(true);
  };
  const docs = data.documents || [];
  const fs = data.final_summary;
  const showDeliveredBanner = order.status === "entregado";
  const logoSrc = brand.logo_url ? `${BACKEND_URL}${brand.logo_url}` : null;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="portal-order">
      {/* Sticky header */}
      <div className="bg-white border-b border-border sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {logoSrc ? (
              <img src={logoSrc} alt="" className="h-9 w-9 rounded object-cover" />
            ) : (
              <div className="h-9 w-9 rounded flex items-center justify-center text-white"
                   style={{ background: "var(--tenant-primary)" }}>
                <Wrench className="h-5 w-5" />
              </div>
            )}
            <div className="leading-tight">
              <div className="text-sm font-semibold" data-testid="portal-tenant-name">{brand.commercial_name}</div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-widest">Portal de seguimiento</div>
            </div>
          </div>
          <button
            onClick={() => fetchOrder(false)}
            className="text-xs text-muted-foreground hover:text-slate-900 flex items-center gap-1"
            data-testid="portal-refresh"
          >
            <RefreshCw className={`h-3.5 w-3.5 ${refreshing ? "animate-spin" : ""}`} />
            {refreshing ? "…" : loadedAt ? timeAgo(loadedAt.toISOString()) : "Actualizar"}
          </button>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-5 space-y-4">
        {/* Hero — estatus prominente */}
        <div className="bg-white border border-border rounded-xl p-6 sm:p-7" data-testid="portal-hero">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Folio</div>
          <div className="text-3xl sm:text-4xl font-semibold tracking-tight mt-1"
               style={{ fontFamily: "IBM Plex Mono, monospace", color: "var(--tenant-primary)" }}
               data-testid="portal-folio">
            {order.folio}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Badge className={`${STATUS_TONE[order.status] || "bg-slate-100"} text-sm px-3 py-1`} data-testid="portal-status">
              {order.stage_label || order.status_label_customer}
            </Badge>
            {order.service_type_label && (
              <Badge variant="secondary" className="text-xs" data-testid="portal-service-type">{order.service_type_label}</Badge>
            )}
          </div>
          {showDeliveredBanner && (
            <div className="mt-4 rounded-md bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-800 flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 shrink-0" />
              <span><b>Tu unidad ya fue entregada.</b> ¡Gracias por confiar en {brand.commercial_name}!</span>
            </div>
          )}
          <dl className="mt-5 grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
            <div>
              <dt className="text-[10px] uppercase text-muted-foreground tracking-wider">Ingreso</dt>
              <dd className="text-slate-900">{order.intake_date} {order.intake_time || ""}</dd>
            </div>
            <div>
              <dt className="text-[10px] uppercase text-muted-foreground tracking-wider">Sucursal</dt>
              <dd className="text-slate-900">{branch.name}</dd>
            </div>
            <div>
              <dt className="text-[10px] uppercase text-muted-foreground tracking-wider">Última actualización</dt>
              <dd className="text-slate-900">{timeAgo(order.last_update)}</dd>
            </div>
          </dl>
          {order.customer_reported_issue && (
            <div className="mt-4 pt-4 border-t border-border">
              <div className="text-[10px] uppercase text-muted-foreground tracking-wider mb-1">Motivo del ingreso</div>
              <div className="text-sm text-slate-800">{order.customer_reported_issue}</div>
            </div>
          )}
        </div>

        {/* Vehículo */}
        <div className="bg-white border border-border rounded-xl p-5">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Tu unidad</div>
          <div className="text-lg font-semibold" data-testid="portal-vehicle">
            {vehicle.brand} {vehicle.model} {vehicle.year || ""}
          </div>
          <div className="text-sm text-muted-foreground mt-1">
            {vehicle.color}
            {vehicle.plates_masked && <>{vehicle.color ? " · " : ""}Placas: <span className="font-mono">{vehicle.plates_masked}</span></>}
          </div>
        </div>

        {/* Historial tipo rastreo */}
        <PortalStatusHistory history={data.status_history} />

        <PortalDiagnosis diagnosis={data.diagnosis} />

        {decisionNotice && (
          <div className={`rounded-xl p-4 text-sm border ${decisionNotice.decision === "approve" ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-900"}`} data-testid="portal-quote-decision-notice">
            {decisionNotice.decision === "approve"
              ? `Autorizaste la cotización ${decisionNotice.number}. El taller ya recibió tu autorización.`
              : `Rechazaste la cotización ${decisionNotice.number}. El taller verá tu respuesta y podrá contactarte.`}
          </div>
        )}
        <PortalQuotes quotes={data.quotes} folio={order.folio} vehicle={[vehicle.brand, vehicle.model, vehicle.year].filter(Boolean).join(" ")} backendUrl={BACKEND_URL} onDecide={decideQuote} />

        <PortalPaymentProof payment={data.payment} onUpload={uploadProof} />

        {/* Evidencias públicas */}
        {evidences.length > 0 && (
          <div className="bg-white border border-border rounded-xl p-5" data-testid="portal-evidences">
            <div className="flex items-center gap-2 mb-3">
              <Camera className="h-4 w-4" style={{ color: "var(--tenant-primary)" }} />
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground">
                Evidencias del trabajo ({evidences.length})
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {evidences.map((ev) => {
                const fileUrl = ev.file_url || ev.url;
                const caption = ev.title || ev.caption;
                const src = fileUrl ? `${BACKEND_URL}${fileUrl}` : null;
                return (
                  <button key={ev.id} className="text-left group" onClick={() => src && setLightbox(src)}
                          data-testid={`portal-evidence-${ev.id}`}>
                    {src ? (
                      <img src={src} alt={caption || ""}
                           className="w-full aspect-square object-cover rounded-md border border-border group-hover:opacity-90 transition-opacity" />
                    ) : (
                      <div className="w-full aspect-square rounded-md border border-border bg-slate-50 flex items-center justify-center">
                        <Camera className="h-6 w-6 text-slate-300" />
                      </div>
                    )}
                    {caption && <div className="text-xs mt-1.5 truncate">{caption}</div>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Documentos */}
        {docs.length > 0 && (
          <div className="bg-white border border-border rounded-xl p-5" data-testid="portal-documents">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">Documentos disponibles</div>
            <div className="space-y-2">
              {docs.map((d) => (
                <a
                  key={d.kind}
                  href={`${BACKEND_URL}${d.url}`}
                  target="_blank" rel="noreferrer"
                  className="flex items-center justify-between gap-3 p-3 border border-border rounded-md hover:border-tenant transition-colors"
                  data-testid={`portal-doc-${d.kind}`}
                >
                  <div className="flex items-center gap-3">
                    <FileText className="h-5 w-5 text-slate-600" />
                    <span className="text-sm font-medium">{d.label}</span>
                  </div>
                  <Download className="h-4 w-4 text-muted-foreground" />
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Resumen final */}
        {fs && (
          <div className="bg-white border border-emerald-200 rounded-xl p-5 bg-emerald-50/40" data-testid="portal-final-summary">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="h-5 w-5 text-emerald-700" />
              <div className="text-sm font-semibold text-emerald-900">Resumen final del servicio</div>
            </div>
            <div className="space-y-3 text-sm">
              {fs.work_done && (
                <div>
                  <div className="text-[10px] uppercase text-muted-foreground tracking-wider">Trabajo realizado</div>
                  <div className="text-slate-800 whitespace-pre-line">{fs.work_done}</div>
                </div>
              )}
              {fs.observations && (
                <div>
                  <div className="text-[10px] uppercase text-muted-foreground tracking-wider">Observaciones</div>
                  <div className="text-slate-800 whitespace-pre-line">{fs.observations}</div>
                </div>
              )}
              {fs.recommendations && (
                <div>
                  <div className="text-[10px] uppercase text-muted-foreground tracking-wider">Recomendaciones</div>
                  <div className="text-slate-800 whitespace-pre-line">{fs.recommendations}</div>
                </div>
              )}
              {fs.next_service_note && (
                <div className="pt-2 border-t border-emerald-200">
                  <div className="text-[10px] uppercase text-emerald-800 tracking-wider">Próxima revisión</div>
                  <div className="text-emerald-900 font-medium">{fs.next_service_note}</div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Contacto */}
        {(branch.phone_masked || branch.address_short) && <div className="bg-white border border-border rounded-xl p-5">
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-3">Contacto del taller</div>
          <div className="space-y-2 text-sm">
            {branch.phone_masked && (
              <div className="flex items-center gap-2 text-slate-700">
                <Phone className="h-4 w-4" />
                <span>Teléfono {branch.phone_masked}</span>
              </div>
            )}
            {branch.address_short && (
              <div className="flex items-start gap-2 text-slate-700">
                <MapPin className="h-4 w-4 mt-0.5 shrink-0" />
                <span>{branch.address_short}</span>
              </div>
            )}
          </div>
        </div>}

        {/* Historial del vehículo — colapsable */}
        {data.vehicle_history_available && (
          <div className="bg-white border border-border rounded-xl" data-testid="portal-history">
            <button
              onClick={loadHistory}
              className="w-full flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
              data-testid="portal-history-toggle"
            >
              <div className="flex items-center gap-2">
                <History className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Historial de este vehículo</span>
                {history && history.count > 0 && (
                  <Badge variant="secondary" className="text-[10px]">{history.count}</Badge>
                )}
              </div>
              {historyOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
            </button>
            {historyOpen && (
              <div className="border-t border-border p-4">
                {loadingHistory ? (
                  <Skeleton className="h-12" />
                ) : history?.items?.length ? (
                  <ul className="divide-y divide-border">
                    {history.items.map((h, i) => (
                      <li key={i} className="py-2.5 flex justify-between items-center gap-3">
                        <div>
                          <div className="text-sm font-medium">{h.service_type_label || "Servicio"}</div>
                          <div className="text-[11px] text-muted-foreground font-mono">{h.folio_masked} · {h.entry_date}</div>
                        </div>
                        <Badge variant="secondary" className="text-[10px] shrink-0">{h.status_customer}</Badge>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    Este es el primer servicio registrado para esta unidad.
                  </p>
                )}
              </div>
            )}
          </div>
        )}

        {/* Contacto del taller — Fase 7 (solo si hay teléfono) */}
        {data.contact?.phone && (
          <div className="bg-white border border-border rounded-xl p-5" data-testid="portal-contact">
            <div className="text-xs uppercase tracking-widest text-muted-foreground mb-3">
              ¿Dudas? Contacta al taller
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href={`tel:${data.contact.phone.replace(/\s+/g, "")}`}
                className="flex-1 flex items-center gap-3 p-3 rounded-lg border border-border hover:bg-slate-50 transition-colors"
                data-testid="portal-contact-phone"
              >
                <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center shrink-0">
                  <Phone className="h-5 w-5 text-slate-700" />
                </div>
                <div>
                  <div className="text-[11px] uppercase text-muted-foreground tracking-wider">Llamar</div>
                  <div className="text-sm font-medium mono">{data.contact.phone}</div>
                </div>
              </a>
              {data.contact.whatsapp_url && (
                <a
                  href={data.contact.whatsapp_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center gap-3 p-3 rounded-lg border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 transition-colors"
                  data-testid="portal-contact-whatsapp"
                >
                  <div className="h-10 w-10 rounded-full bg-emerald-600 flex items-center justify-center shrink-0">
                    <MessageCircle className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <div className="text-[11px] uppercase text-emerald-800 tracking-wider">WhatsApp</div>
                    <div className="text-sm font-medium text-emerald-900">Escribir mensaje</div>
                  </div>
                </a>
              )}
            </div>
            {data.contact.branch_name && (
              <div className="mt-3 text-xs text-muted-foreground">
                Atención en <span className="font-medium">{data.contact.branch_name}</span>
              </div>
            )}
          </div>
        )}

        <div className="mt-6 mb-4 flex flex-col items-center gap-2">
          <img
            src="/logo-principal.png"
            alt="TallerOS"
            data-testid="portal-powered-logo"
            className="h-6 w-6 rounded object-contain opacity-70"
          />
          <div className="text-[10px] text-muted-foreground uppercase tracking-widest">
            Powered by TallerOS · by VRIntegrales
          </div>
        </div>
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 bg-slate-900/90 flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setLightbox(null)}
          data-testid="portal-lightbox"
        >
          <img src={lightbox} alt="" className="max-w-full max-h-full object-contain" />
        </div>
      )}
    </div>
  );
}
